/**
 * Build one print-ready HTML document out of the parts of a member's record a
 * caseworker picked in the Print dialog (TRUS-636): profile sections, forms,
 * case notes, outreach and documents.
 *
 * Two modes:
 *   - **filled** — the member's current information. With `emptyAsWriteIn`,
 *     a field that has no value prints as a write-in line / unticked options
 *     instead of being left out, so the gaps can be collected on paper during
 *     a visit and typed in afterwards.
 *   - **blank** — a hand-fill packet: labels, lines and option boxes only. Case
 *     notes and outreach become blank note sheets and an empty contact log.
 *     Only the member's name and number are printed (so the pages can be
 *     filed); no other member data is.
 *
 * Pure: no fetching, no DOM. The dialog loads the data and passes translated
 * labels; this returns a string for printInNewWindow.
 */

import type { FormTemplate } from '@/services/api/forms';
import {
  buildFormSectionsHtml,
  checkboxRow,
  escapeHtml,
  fieldBlock,
  writingBox,
  writingLine,
} from '@/components/forms/formPrintBuilder';
import { normalizeOptions, type RawOption } from '@/components/forms/fieldOptions';

export type PacketMode = 'filled' | 'blank';

export interface PacketProfileField {
  key: string;
  label: string;
  /** ProfileField.field_type: str, long_text, select, multi_select, bool, date, ... */
  type: string;
  options?: RawOption[] | null;
  value?: unknown;
  required?: boolean;
}

export interface PacketProfileSection {
  key: string;
  title: string;
  fields: PacketProfileField[];
}

export interface PacketForm {
  title: string;
  subtitle?: string | null;
  template: Pick<FormTemplate, 'sections'>;
  fieldData?: Record<string, any> | null;
}

export interface PacketNote {
  date?: string | null;
  author?: string | null;
  type?: string | null;
  subject?: string | null;
  body?: string | null;
  programName?: string | null;
}

export interface PacketOutreach {
  date?: string | null;
  channel?: string | null;
  direction?: string | null;
  minutes?: number | null;
  engagementType?: string | null;
  location?: string | null;
  notes?: string | null;
  formName?: string | null;
}

export interface PacketDocument {
  name: string;
  type?: string | null;
  programName?: string | null;
  uploadedAt?: string | null;
  uploadedBy?: string | null;
  notes?: string | null;
  /** data: URL of an image document, printed on its own page. */
  imageDataUrl?: string | null;
}

export interface PacketLabels {
  packetTitle: string;
  blankPacketTitle: string;
  member: string;
  memberNumber: string;
  dateOfBirth: string;
  program: string;
  printed: string;
  printedBy: string;
  confidential: string;
  profile: string;
  forms: string;
  caseNotes: string;
  caseNote: string;
  outreach: string;
  documents: string;
  date: string;
  author: string;
  type: string;
  subject: string;
  notes: string;
  contactMethod: string;
  contactMethods: string[];
  followUp: string;
  followUpDate: string;
  yes: string;
  no: string;
  staffSignature: string;
  channel: string;
  direction: string;
  minutes: string;
  location: string;
  form: string;
  name: string;
  uploaded: string;
  uploadedBy: string;
  documentsNote: string;
  noItems: string;
  checkOne: string;
  checkAll: string;
  filledBy: string;
}

export const DEFAULT_PACKET_LABELS: PacketLabels = {
  packetTitle: 'Member Record',
  blankPacketTitle: 'Member Record — Hand-fill Copy',
  member: 'Member',
  memberNumber: 'Member #',
  dateOfBirth: 'Date of birth',
  program: 'Program',
  printed: 'Printed',
  printedBy: 'Printed by',
  confidential: 'Confidential — contains protected member information. Store securely and shred when no longer needed.',
  profile: 'Member Profile',
  forms: 'Forms',
  caseNotes: 'Case Notes',
  caseNote: 'Case Note',
  outreach: 'Outreach',
  documents: 'Documents',
  date: 'Date',
  author: 'Staff',
  type: 'Type',
  subject: 'Subject',
  notes: 'Notes',
  contactMethod: 'Contact method',
  contactMethods: ['In person', 'Phone', 'Text', 'Email', 'Other'],
  followUp: 'Follow-up needed',
  followUpDate: 'Follow-up date',
  yes: 'Yes',
  no: 'No',
  staffSignature: 'Staff signature',
  channel: 'Channel',
  direction: 'Direction',
  minutes: 'Minutes',
  location: 'Location',
  form: 'Form',
  name: 'Name',
  uploaded: 'Uploaded',
  uploadedBy: 'Uploaded by',
  documentsNote: 'Only image files print here. Open PDFs and other files from the Documents tab to print them.',
  noItems: 'None on file.',
  checkOne: 'Check one',
  checkAll: 'Check all that apply',
  filledBy: 'Filled in by',
};

export interface BuildPrintPacketArgs {
  mode: PacketMode;
  /** Filled mode: print empty fields as write-in lines instead of skipping them. */
  emptyAsWriteIn?: boolean;
  header: {
    memberName?: string | null;
    memberNumber?: string | null;
    dateOfBirth?: string | null;
    programName?: string | null;
    printedBy?: string | null;
    printedAt: Date;
  };
  profile?: PacketProfileSection[];
  forms?: PacketForm[];
  /** Filled: the notes to print. Blank: ignored; `blankNotePages` sheets print instead. */
  notes?: PacketNote[];
  blankNotePages?: number;
  outreach?: PacketOutreach[];
  blankOutreachRows?: number;
  documents?: PacketDocument[];
  labels?: Partial<PacketLabels>;
}

/** Option lists longer than this print as a single value / write-in line (states, countries…). */
export const MAX_PRINTED_OPTIONS = 12;

const OPTION_TYPES = new Set(['select', 'radio', 'multi_select', 'checkbox']);
const MULTI_TYPES = new Set(['multi_select', 'checkbox']);
const BOOL_TYPES = new Set(['bool', 'toggle']);
const DATE_TYPES = new Set(['date', 'datetime', 'fixed_date']);
const LONG_TYPES = new Set(['long_text', 'textarea']);

export function isEmptyPacketValue(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v as object).length === 0;
  return false;
}

/** Multi-select values are stored as arrays, JSON-array strings, or a bare string. */
export function parseMultiValue(v: unknown): string[] {
  if (isEmptyPacketValue(v)) return [];
  if (Array.isArray(v)) return v.map((x) => String(x));
  if (typeof v === 'string') {
    const s = v.trim();
    if (s.startsWith('[')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) return parsed.map((x) => String(x));
      } catch {
        /* not JSON — fall through */
      }
    }
    return [s];
  }
  return [String(v)];
}

const truthy = (v: unknown): boolean =>
  v === true || (typeof v === 'string' && ['true', 'yes', '1', 'y'].includes(v.trim().toLowerCase())) || v === 1;

/** YYYY-MM-DD… → MM/DD/YYYY without a timezone shift; anything else as-is. */
export function formatPacketDate(v: unknown): string {
  if (isEmptyPacketValue(v)) return '';
  const s = String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : s;
}

const matches = (opt: { value: string; label: string }, v: string) =>
  opt.value === v || opt.label.toLowerCase() === v.toLowerCase();

const valueBox = (html: string) =>
  `<div style="padding:5px 8px;border:1px solid #d1d5db;border-radius:4px;font-size:13px;min-height:22px;white-space:pre-wrap">${html}</div>`;

/** A profile field, or '' when it is empty and empty fields are being skipped. */
export function renderProfileFieldHtml(
  field: PacketProfileField,
  mode: PacketMode,
  emptyAsWriteIn: boolean,
  L: PacketLabels = DEFAULT_PACKET_LABELS,
): { html: string; wide: boolean } {
  const empty = isEmptyPacketValue(field.value);
  if (mode === 'filled' && empty && !emptyAsWriteIn) return { html: '', wide: false };
  const blank = mode === 'blank' || empty;
  const label = `${escapeHtml(field.label)}${field.required ? ' <span style="color:#dc2626">*</span>' : ''}`;
  const opts = normalizeOptions(field.options ?? undefined);
  const isMulti = MULTI_TYPES.has(field.type);

  if (OPTION_TYPES.has(field.type) && opts.length) {
    const selected = blank ? [] : isMulti ? parseMultiValue(field.value) : [String(field.value)];
    if (opts.length <= MAX_PRINTED_OPTIONS) {
      const rows = opts.map((o) => checkboxRow(o.label, selected.some((v) => matches(o, v)))).join('');
      return { html: fieldBlock(label, rows, blank ? (isMulti ? L.checkAll : L.checkOne) : undefined), wide: true };
    }
    if (blank) return { html: fieldBlock(label, writingLine(24)), wide: false };
    const shown = selected.map((v) => opts.find((o) => matches(o, v))?.label ?? v);
    return { html: fieldBlock(label, valueBox(escapeHtml(shown.join(', ')))), wide: false };
  }

  if (BOOL_TYPES.has(field.type) || (field.type === 'checkbox' && !opts.length)) {
    const on = !blank && truthy(field.value);
    const off = !blank && !on;
    return {
      html: fieldBlock(
        label,
        `<div style="display:flex;gap:18px">${checkboxRow(L.yes, on)}${checkboxRow(L.no, off)}</div>`,
      ),
      wide: false,
    };
  }

  if (LONG_TYPES.has(field.type)) {
    return {
      html: fieldBlock(label, blank ? writingBox(70) : valueBox(escapeHtml(field.value))),
      wide: true,
    };
  }

  if (blank) return { html: fieldBlock(label, writingLine(24)), wide: false };
  const display = DATE_TYPES.has(field.type) ? formatPacketDate(field.value) : String(field.value);
  return { html: fieldBlock(label, valueBox(escapeHtml(display))), wide: false };
}

function profileSectionHtml(
  section: PacketProfileSection,
  mode: PacketMode,
  emptyAsWriteIn: boolean,
  L: PacketLabels,
): string {
  const cells = section.fields
    .map((f) => renderProfileFieldHtml(f, mode, emptyAsWriteIn, L))
    .filter((c) => c.html)
    .map((c) => `<div${c.wide ? ' style="grid-column:1 / -1"' : ''}>${c.html}</div>`)
    .join('');
  if (!cells) return '';
  return card(section.title, `<div style="display:grid;grid-template-columns:1fr 1fr;column-gap:24px">${cells}</div>`);
}

const card = (title: string, body: string, subtitle?: string | null) => `
  <div class="card">
    <div class="card-head">
      <h3>${escapeHtml(title)}</h3>
      ${subtitle ? `<p>${escapeHtml(subtitle)}</p>` : ''}
    </div>
    <div class="card-body">${body}</div>
  </div>`;

const metaLine = (parts: Array<string | null | undefined>) =>
  parts.filter((p) => p != null && String(p).trim() !== '').map((p) => escapeHtml(p)).join(' · ');

function notesHtml(notes: PacketNote[], L: PacketLabels): string {
  if (!notes.length) return `<p class="muted">${escapeHtml(L.noItems)}</p>`;
  return notes
    .map(
      (n) => `
      <div class="note">
        <div class="note-meta">${metaLine([formatPacketDate(n.date), n.type, n.author, n.programName])}</div>
        ${n.subject ? `<div class="note-subject">${escapeHtml(n.subject)}</div>` : ''}
        <div class="note-body">${escapeHtml(n.body)}</div>
      </div>`,
    )
    .join('');
}

function blankNoteSheet(L: PacketLabels): string {
  const methods = L.contactMethods.map((m) => checkboxRow(m, false)).join('');
  return card(
    L.caseNote,
    `<div style="display:grid;grid-template-columns:1fr 1fr;column-gap:24px">
       <div>${fieldBlock(escapeHtml(L.date), writingLine(24))}</div>
       <div>${fieldBlock(escapeHtml(L.author), writingLine(24))}</div>
     </div>
     ${fieldBlock(escapeHtml(L.contactMethod), `<div style="display:flex;flex-wrap:wrap;gap:4px 18px">${methods}</div>`)}
     ${fieldBlock(escapeHtml(L.subject), writingLine(24))}
     ${fieldBlock(escapeHtml(L.notes), writingBox(360))}
     <div style="display:grid;grid-template-columns:1fr 1fr;column-gap:24px">
       <div>${fieldBlock(escapeHtml(L.followUp), `<div style="display:flex;gap:18px">${checkboxRow(L.yes, false)}${checkboxRow(L.no, false)}</div>`)}</div>
       <div>${fieldBlock(escapeHtml(L.followUpDate), writingLine(24))}</div>
     </div>
     ${fieldBlock(escapeHtml(L.staffSignature), writingLine(36))}`,
  );
}

function outreachTable(rows: PacketOutreach[], L: PacketLabels, blankRows: number): string {
  const head = [L.date, L.channel, L.direction, L.minutes, L.type, L.location, L.notes]
    .map((h) => `<th>${escapeHtml(h)}</th>`)
    .join('');
  const body = rows.length
    ? rows
        .map(
          (r) => `<tr>
            <td>${escapeHtml(formatPacketDate(r.date))}</td>
            <td>${escapeHtml(r.channel)}</td>
            <td>${escapeHtml(r.direction)}</td>
            <td>${r.minutes != null ? escapeHtml(r.minutes) : ''}</td>
            <td>${escapeHtml(r.engagementType)}</td>
            <td>${escapeHtml(r.location)}</td>
            <td style="white-space:pre-wrap">${escapeHtml([r.notes, r.formName ? `${L.form}: ${r.formName}` : ''].filter(Boolean).join('\n'))}</td>
          </tr>`,
        )
        .join('')
    : Array.from({ length: blankRows }, () => `<tr class="blank-row">${'<td></td>'.repeat(7)}</tr>`).join('');
  return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

function documentsHtml(docs: PacketDocument[], L: PacketLabels): string {
  if (!docs.length) return `<p class="muted">${escapeHtml(L.noItems)}</p>`;
  const head = [L.name, L.type, L.program, L.uploaded, L.uploadedBy, L.notes]
    .map((h) => `<th>${escapeHtml(h)}</th>`)
    .join('');
  const rows = docs
    .map(
      (d) => `<tr>
        <td>${escapeHtml(d.name)}</td>
        <td>${escapeHtml(d.type)}</td>
        <td>${escapeHtml(d.programName)}</td>
        <td>${escapeHtml(formatPacketDate(d.uploadedAt))}</td>
        <td>${escapeHtml(d.uploadedBy)}</td>
        <td style="white-space:pre-wrap">${escapeHtml(d.notes)}</td>
      </tr>`,
    )
    .join('');
  const images = docs
    .filter((d) => d.imageDataUrl && /^data:image\//.test(d.imageDataUrl))
    .map(
      (d) => `<div class="doc-image">
        <h3>${escapeHtml(d.name)}</h3>
        <img src="${escapeHtml(d.imageDataUrl)}" alt="${escapeHtml(d.name)}" />
      </div>`,
    )
    .join('');
  return `<p class="muted">${escapeHtml(L.documentsNote)}</p>
    <table><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>${images}`;
}

export function buildPrintPacketHtml(args: BuildPrintPacketArgs): string {
  const L: PacketLabels = { ...DEFAULT_PACKET_LABELS, ...(args.labels || {}) };
  const { mode, header } = args;
  const blank = mode === 'blank';
  const emptyAsWriteIn = blank || !!args.emptyAsWriteIn;

  const runHead = metaLine([
    header.memberName ? `${L.member}: ${header.memberName}` : null,
    header.memberNumber ? `${L.memberNumber} ${header.memberNumber}` : null,
    header.programName ? `${L.program}: ${header.programName}` : null,
  ]);

  const parts: Array<{ title: string; body: string }> = [];

  if (args.profile) {
    const body = args.profile.map((s) => profileSectionHtml(s, mode, emptyAsWriteIn, L)).join('');
    parts.push({ title: L.profile, body: body || `<p class="muted">${escapeHtml(L.noItems)}</p>` });
  }

  for (const f of args.forms || []) {
    parts.push({
      title: f.title,
      body:
        (f.subtitle ? `<p class="muted">${escapeHtml(f.subtitle)}</p>` : '') +
        buildFormSectionsHtml(f.template, blank ? 'hard-copy-blank' : 'filled', blank ? undefined : f.fieldData ?? {}, {
          emptyAsWriteIn,
        }),
    });
  }

  if (args.notes) {
    const body = blank
      ? Array.from({ length: Math.max(1, args.blankNotePages ?? 1) }, () => blankNoteSheet(L)).join(
          '<div class="page-break"></div>',
        )
      : notesHtml(args.notes, L);
    parts.push({ title: L.caseNotes, body });
  }

  if (args.outreach) {
    parts.push({
      title: L.outreach,
      body: outreachTable(blank ? [] : args.outreach, L, args.blankOutreachRows ?? 15),
    });
  }

  if (args.documents && !blank) {
    parts.push({ title: L.documents, body: documentsHtml(args.documents, L) });
  }

  const title = blank ? L.blankPacketTitle : L.packetTitle;
  const coverMeta = blank
    ? `<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;font-size:12px;margin-bottom:8px">
         <div><div class="muted">${escapeHtml(L.filledBy)}</div>${writingLine()}</div>
         <div><div class="muted">${escapeHtml(L.date)}</div>${writingLine()}</div>
         <div><div class="muted">${escapeHtml(L.memberNumber)}</div>${
           header.memberNumber ? `<div style="padding-top:2px">${escapeHtml(header.memberNumber)}</div>` : writingLine()
         }</div>
       </div>`
    : '';

  const cover = `
    <h1>${escapeHtml(title)}</h1>
    <p class="cover-meta">${metaLine([
      header.memberName,
      header.memberNumber ? `${L.memberNumber} ${header.memberNumber}` : null,
      !blank && header.dateOfBirth ? `${L.dateOfBirth} ${formatPacketDate(header.dateOfBirth)}` : null,
      header.programName,
    ])}</p>
    <p class="muted">${metaLine([
      `${L.printed} ${header.printedAt.toLocaleString()}`,
      header.printedBy ? `${L.printedBy} ${header.printedBy}` : null,
    ])}</p>
    ${coverMeta}
    ${blank ? '' : `<p class="confidential">${escapeHtml(L.confidential)}</p>`}`;

  const partsHtml = parts
    .map(
      (p, i) => `
      <section class="part${i > 0 ? ' break' : ''}">
        ${i > 0 && runHead ? `<div class="run-head">${runHead}</div>` : ''}
        <h2>${escapeHtml(p.title)}</h2>
        ${p.body}
      </section>`,
    )
    .join('');

  return `<!DOCTYPE html><html><head>
    <meta charset="utf-8" />
    <title>${escapeHtml([title, header.memberName].filter(Boolean).join(' — '))}</title>
    <style>
      @page { margin: 0.6in; }
      * { box-sizing: border-box; }
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1a1a1a; line-height: 1.45; margin: 0; padding: 0; }
      h1 { font-size: 20px; margin: 0 0 4px; }
      h2 { font-size: 17px; margin: 18px 0 10px; padding-bottom: 4px; border-bottom: 2px solid #111827; }
      .cover-meta { font-size: 14px; margin: 0 0 2px; font-weight: 600; }
      .muted { font-size: 12px; color: #6b7280; margin: 0 0 8px; }
      .confidential { font-size: 11px; color: #92400e; background: #fffbeb; border: 1px solid #fcd34d; border-radius: 4px; padding: 6px 8px; }
      .part.break { page-break-before: always; break-before: page; }
      .page-break { page-break-before: always; break-before: page; }
      .run-head { font-size: 10px; color: #6b7280; text-align: right; }
      .card { margin-bottom: 16px; border: 1px solid #d1d5db; border-radius: 8px; page-break-inside: avoid; break-inside: avoid; }
      .card-head { padding: 8px 14px; background: #f9fafb; border-bottom: 1px solid #d1d5db; }
      .card-head h3 { margin: 0; font-size: 14px; }
      .card-head p { margin: 2px 0 0; font-size: 12px; color: #6b7280; }
      .card-body { padding: 12px 14px; }
      .note { border-bottom: 1px solid #e5e7eb; padding: 8px 0; page-break-inside: avoid; break-inside: avoid; }
      .note-meta { font-size: 11px; color: #6b7280; }
      .note-subject { font-weight: 600; font-size: 13px; }
      .note-body { font-size: 13px; white-space: pre-wrap; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { border: 1px solid #d1d5db; padding: 4px 6px; text-align: left; vertical-align: top; }
      th { background: #f3f4f6; }
      tr { page-break-inside: avoid; break-inside: avoid; }
      tr.blank-row td { height: 34px; }
      .doc-image { page-break-before: always; break-before: page; }
      .doc-image h3 { font-size: 14px; margin: 0 0 8px; }
      .doc-image img { max-width: 100%; max-height: 9in; }
    </style>
  </head><body>
    ${cover}
    ${partsHtml}
  </body></html>`;
}
